import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { apiRequest } from '../api';
import { QRCodeCanvas } from 'qrcode.react';
import API_BASE_URL from '../apiConfig';
import './Deposit.css';
import { commonIcons } from './assetImports';
import { useTranslation } from 'react-i18next';

const Deposit = ({ setFooterVisible }) => {
    const { user } = useContext(AuthContext);
    const { t } = useTranslation('depositWithdrawAll');
    const [walletAddress, setWalletAddress] = useState('');
    const [showPopup, setShowPopup] = useState(false);
    const [selectedCurrency, setSelectedCurrency] = useState(null);
    const [message, setMessage] = useState('');
    const [copied, setCopied] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    const trxRate = 0.3; 

    const getTRXAmount = (usdtAmount) => {
        return Math.ceil(usdtAmount / trxRate);
    };

    useEffect(() => {
        const fetchWalletAddress = async () => {
            try {
                const response = await apiRequest(`${API_BASE_URL}/api/wallet/wallet-address`, {
                    method: 'GET',
                    credentials: 'include',
                });
                const data = await response.json();
                if (response.ok) {
                    setWalletAddress(data.walletAddress);
                } else {
                    setMessage(t('depositUSDT.fetchWalletError', { error: data.error }));
                }
            } catch (err) {
                setMessage(t('depositUSDT.fetchWalletErrorGeneric'));
            }
        };
        fetchWalletAddress();
    }, []);

    const handleCopy = () => {
        navigator.clipboard.writeText(walletAddress).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }, (err) => {
            setMessage(t('depositUSDT.copyError'));
        });
    };

    const openPopup = (currency) => {
        setSelectedCurrency(currency);
        setShowPopup(true);
    };

    const handleClosePopup = () => {
        setIsClosing(true);
        setTimeout(() => {
            setShowPopup(false);
            setIsClosing(false);
        }, 200);
    };

    return (
        <div className="depositPage-container">
            <h1 className="depositPage-title">{t('depositUSDT.title')}</h1>

            <div className="depositPage-instructions-section">
                <h3 className="depositPage-instructions-title">{t('depositUSDT.howToDeposit')}</h3>
                <div className="depositPage-instructions-border">
                    <div className="depositPage-instruction-item">
                        <div className="depositPage-instruction-number">1</div>
                        <span>{t('depositUSDT.step1')}</span>
                    </div>
                    <div className="depositPage-instruction-item">
                        <div className="depositPage-instruction-number">2</div>
                        <span>{t('depositUSDT.step2')}</span>
                    </div>
                    <div className="depositPage-instruction-item">
                        <div className="depositPage-instruction-number">3</div>
                        <span>{t('depositUSDT.step3')}</span>
                    </div>
                </div>
            </div>

            <div className="depositPage-currency-section">
                <div className="depositPage-currency-option" onClick={() => openPopup('USDT')}>
                    <img src={commonIcons.usdtSvg} alt={t('depositUSDT.usdtAlt')} className="depositPage-currency-icon" />
                    <span>{t('depositUSDT.usdtTrc20')}</span>
                </div>
                <div className="depositPage-currency-option" onClick={() => openPopup('TRX')}>
                    <img src={commonIcons.trxIcon} alt={t('depositUSDT.trxAlt')} className="depositPage-currency-icon" />
                    <span>{t('depositUSDT.trx')}</span>
                </div>
                <div className="depositPage-currency-option depositPage-coming-soon">
                    <img src={commonIcons.tonIcon} alt={t('depositUSDT.tonAlt')} className="depositPage-currency-icon" />
                    <span>{t('depositUSDT.ton')}</span>
                    <div className="depositPage-coming-soon-label">({t('depositUSDT.comingSoon')})</div>
                </div>
            </div>

            {showPopup && (
                <div className="depositPage-popup-overlay" onClick={handleClosePopup}>
                    <div className={`depositPage-popup-container ${isClosing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
                        <div className="depositPage-popup">
                            <div className="depositPage-popup-header">
                                <img 
                                    src={commonIcons[`${selectedCurrency.toLowerCase()}Icon`]} 
                                    alt={t(`deposit.${selectedCurrency.toLowerCase()}Alt`)} 
                                    className="depositPage-popup-currency-icon" 
                                />
                                <h3 className="depositPage-popup-currency-label">{selectedCurrency}</h3>
                                <button className="depositPage-popup-close" onClick={handleClosePopup}>&times;</button>
                            </div>
                            <p className="depositPage-popup-warning">
                                {selectedCurrency === 'TRX'
                                    ? t('depositUSDT.trxWarning')
                                    : t('depositUSDT.usdtWarning')
                                }
                            </p>
                            <div className="depositPage-qr-section">
                                <QRCodeCanvas 
                                    value={walletAddress} 
                                    size={160} 
                                    fgColor="#FFFFFF" 
                                    bgColor="#282828" 
                                />
                                <p className="depositPage-wallet-address-text">{walletAddress}</p>
                            </div>
                            <button onClick={handleCopy} className="base-item-box depositPage-copy-button">
                                <img src={commonIcons.copyIcon} alt={t('depositUSDT.copyAlt')} className="depositPage-copy-icon" /> 
                                {t('depositUSDT.copyAddress')}
                            </button>
                            {copied && <p className="depositPage-copied-message">{t('depositUSDT.addressCopied')}</p>}
                            <p className="depositPage-minimum-amount">
                                {selectedCurrency === 'TRX' && (
                                    <p className="depositPage-rate-info">
                                        1 TRX ≈ {trxRate.toFixed(3)} USDT
                                    </p>
                                )}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {message && <p className="depositPage-error-message">{message}</p>}
        </div>
    );
};

export default Deposit;